import { FETCH_LIMIT } from '@constants/limits';
import { remove, send } from './client';
import type { CylinderStatus, FillState, LocationType, PositionStatus, SessionKind, Severity, ShipmentStatus } from './common';
import type { CylinderTest } from './cylinderTests';
import { query, type Cursor, type CursorPage } from './paging';

/** The periodic retest as the server reads it (WIB): no date on record, fine, due within 30 days, or past. */
export type TestState = 'none' | 'ok' | 'soon' | 'overdue';
/** List filter `test`: past, due within 30 days, or either (`due`). */
export type TestFilter = 'overdue' | 'soon' | 'due';
export const TEST_FILTERS: readonly Exclude<TestFilter, 'due'>[] = ['overdue', 'soon'];
/** List filter `notSeenFor`: no Reader read it for that long, or never. */
export const NOT_SEEN_FOR = ['7d', '30d', '90d', 'never'] as const;
export type NotSeenFor = (typeof NOT_SEEN_FOR)[number];
/** Taken out of circulation: every status but ACTIVE (DESTROYED for good; the backend's "Nonaktif" tab). */
export const INACTIVE_STATUSES: readonly CylinderStatus[] = ['WITHDRAWN', 'DAMAGED', 'LOST', 'DESTROYED'];

/** A cylinder as GET /cylinders lists it (the backend's CylinderListItem). Dates are YYYY-MM-DD. */
export interface Cylinder {
  id: number;
  serialNo: string;
  epc: string;
  tid: string | null;
  kind: string;
  capacity: string | null;
  manufacturer: string | null;
  /** the number the manufacturer stamped on the cylinder (optional, unique) */
  manufacturerSerial?: string | null;
  manufacturedOn: string | null;
  lastTestOn: string | null;
  nextTestDue: string | null;
  owner: string | null;
  status: CylinderStatus;
  notes: string | null;
  /** where its delivery notes put it; null while in transit or missing */
  currentLocationId: number | null;
  positionStatus: PositionStatus;
  currentShipmentId: number | null;
  lastSeenAt: string | null;
  lastGwId: string | null;
  lastSeenLocationId: number | null;
  registeredAt: string;
  updatedAt: string;
  /** put on a Surat Jalan or moved at least once: no longer a registration mistake, so it cannot be deleted */
  everShipped: boolean;
  /** full or empty: set by the server when a Surat Jalan departs with it (registered cylinders are EMPTY) */
  fillState?: FillState;
  locationName: string | null;
  locationType: LocationType | null;
  currentShipmentNumber: string | null;
  isDemo: boolean;
  /** the retest date has passed (WIB) */
  testOverdue: boolean;
  /** where it is, or was last seen while in transit or missing (what the province filter matches) */
  placeLocationId: number | null;
  /** the periodic retest, decided by the server in WIB: none on record, fine, due within 30 days, or past */
  testState: TestState;
  /** the region of where it counts (placeLocationId) */
  regencyName: string | null;
  provinceName: string | null;
  /** where and by which Reader it was read last */
  lastSeenLocationName: string | null;
  lastGwLabel: string | null;
}

/**
 * One step of the cylinder's journey, newest first: a movement, or (last, when the whole journey fits) its
 * registration at the owner warehouse (`id` null, `to*` the warehouse). `lat`/`lng` are the destination's.
 */
export interface JourneyStep {
  kind: 'movement' | 'registered';
  id: number | null;
  at: string;
  gwId: string | null;
  shipmentId: number | null;
  shipmentNumber: string | null;
  fromLocationId: number | null;
  fromName: string | null;
  fromType: LocationType | null;
  fromLat: number | null;
  fromLng: number | null;
  toLocationId: number | null;
  toName: string | null;
  toType: LocationType | null;
  lat: number | null;
  lng: number | null;
}

export interface CylinderEvent {
  id: string;
  type: string;
  ts: string;
  gwId: string;
  tid: string | null;
  /** as the gateway sent it (MQTT contract keys, e.g. dwell_ms) */
  data: Record<string, unknown>;
  locationName: string | null;
  gwLabel: string | null;
  /** the read loaded or unloaded it on a delivery note */
  context: SessionKind | null;
  shipmentNumber: string | null;
}

export interface CylinderAlert {
  id: number;
  type: string;
  severity: Severity;
  title: string | null;
  gwId: string | null;
  detail: Record<string, unknown>;
  createdAt: string;
  resolvedAt: string | null;
  ackedAt: string | null;
}

export interface CylinderShipment {
  id: number;
  number: string | null;
  status: ShipmentStatus;
  isDemo: boolean;
  originLocationId: number;
  originName: string;
  destinationLocationId: number;
  destinationName: string;
  departedAt: string | null;
  receivedAt: string | null;
  loadedAt: string;
  itemReceivedAt: string | null;
  outcome: 'RECEIVED' | 'MISSING' | null;
  manual: boolean;
}

/** GET /cylinders/:id and /cylinders/by-epc/:epc: every list bounded, newest first; the whole lists are paged apart. */
/** A household as the traceability answers name it: the head's name full, masked ("S•••") or null by role; a
 * household of a small village (fewer than 5) all null for a caller without pii.read. */
export interface TraceHousehold { id: number | null; headName: string | null; villageCode: string | null; villageName: string | null }

/** Who holds a cylinder now and where it got it (GET /cylinders/:id › custody, Phase 4). */
export interface CylinderCustody {
  positionStatus: PositionStatus;
  /** its last fill (the permanent fill record): which SPBG, when (absent from older backends) */
  lastFill?: { at: string; location: { id: number; name: string } } | null;
  holder: TraceHousehold | null;
  /** since when the holder has it */
  since: string | null;
  /** the exchange that last handed it over ("beli dari mana"); `transferred` when another household holds it now */
  delivered: { orderId: number; orderNumber: string; channel: 'APP' | 'COUNTER'; hub: { id: number; name: string }; at: string; transferred: boolean } | null;
}

export type CylinderHistoryKind = 'registered' | 'movement' | 'handed_over' | 'empty_returned' | 'empty_received' | 'reconciliation' | 'transferred' | 'sighting' | 'test' | 'retagged';
/** One entry of GET /cylinders/:id/history (newest first): every kind of thing that happened to it. */
export interface CylinderHistoryEntry {
  id: string;
  kind: CylinderHistoryKind;
  at: string;
  location: { id: number; name: string; type: LocationType } | null;
  from: { id: number; name: string } | null;
  shipment: { id: number; number: string | null; kind: 'DISTRIBUTION' | 'WITHDRAWAL' } | null;
  order: { id: number; number: string; channel: 'APP' | 'COUNTER'; fulfilment: 'DELIVERY' | 'PICKUP' } | null;
  household: TraceHousehold | null;
  via: 'COURIER' | 'COUNTER' | 'READER' | 'MANUAL' | null;
  by: { id: number; role: string; name: string } | null;
  detail: string | null;
  method: 'QR' | 'PHONE_READER' | 'MANUAL' | null;
}
/** A keyset page of the history: forward only (`next`), no total. */
export interface CylinderHistoryPage { items: CylinderHistoryEntry[]; meta: { limit: number; next: string | null; prev: null; total: null } }

export interface CylinderDetail extends Cylinder {
  /** the household holding it and where it got it (Phase 4 backends) */
  custody?: CylinderCustody;
  /** in front of a reader right now */
  present: boolean | null;
  presentGwId: string | null;
  /** the TID last read with its tag */
  lastReadTid: string | null;
  /** its last movement: since when it is where it is */
  atLocationSince: string | null;
  journey: JourneyStep[];
  events: CylinderEvent[];
  alerts: CylinderAlert[];
  shipments: CylinderShipment[];
  /** its latest test results (at most 5; all: GET /cylinders/:id/tests); absent from an older backend */
  tests?: CylinderTest[];
  /** how many Surat Jalan carried it, movements it made, alerts about it are open (the whole lists' sizes) */
  shipmentCount: number;
  movementCount: number;
  openAlertCount: number;
}

/**
 * GET /cylinders/summary: the tabs' counts in the caller's scope, each equal to the list with the same place filters
 * and the tab's own (active: ACTIVE; the positions and testDue: ACTIVE ones; inactive: WITHDRAWN + DAMAGED + LOST).
 */
export interface CylinderSummary {
  total: number;
  active: number;
  atLocation: number;
  inTransit: number;
  missing: number;
  /** ACTIVE and with a household (Phase 3); absent from an older backend */
  withConsumer?: number;
  /** ACTIVE stock recorded at a Titik Distribusi, full and empty (atLocation = stockFull + stockEmpty) */
  stockFull?: number;
  stockEmpty?: number;
  testDue: number;
  testOverdue: number;
  inactive: number;
  /** the retest window of `testDue` and of testState "soon" (days) */
  testDueDays: number;
}
export type CylinderSummaryFilter = Pick<CylinderFilter, 'provinceCode' | 'regencyCode' | 'districtCode' | 'villageCode' | 'locationId'>;

/** The whole lists of one cylinder, newest first, a cursor page at a time (reads are keyed by their ULID). */
export type CylinderJourneyPage = CursorPage<JourneyStep>;
export type CylinderEventPage = CursorPage<CylinderEvent, string>;
export type CylinderShipmentPage = CursorPage<CylinderShipment>;

/** POST /cylinders/:id/status: always with the reason (kept in the activity log); never DESTROYED. */
export interface CylinderStatusChange { status: Exclude<CylinderStatus, 'DESTROYED'>; reason: string }

export type CylinderArea = 'warehouse' | 'distribution';
/**
 * GET /cylinders filters (newest registered first). `status` and `locationType` take several values comma-separated;
 * `test` (overdue | soon | due) supersedes `testDue`, `locationType` supersedes `area` (both still accepted).
 */
export interface CylinderFilter {
  status?: CylinderStatus | string;
  positionStatus?: PositionStatus | '';
  locationId?: number;
  provinceCode?: string;
  regencyCode?: string;
  /** a district (kecamatan) or village (desa / kelurahan) of where it counts */
  districtCode?: string;
  villageCode?: string;
  locationType?: string;
  /** full or empty */
  fillState?: FillState | '';
  area?: CylinderArea | '';
  test?: TestFilter | '';
  testDue?: boolean;
  notSeenFor?: NotSeenFor | '';
  /** held by a household past DORMANT_DAYS without an exchange */
  dormant?: boolean;
  /** part of the serial number or the EPC */
  q?: string;
}
export type CylinderPage = CursorPage<Cylinder>;

/**
 * What a person edits; null clears an optional field. Never the serial number (issued) nor the tag (`epc`, `tid`: the
 * backend refuses them, a tag changes only through retagCylinder). `status` only with cylinder.status: the page changes
 * it through setCylinderStatus, with a reason.
 */
export type CylinderPatch = Partial<Pick<Cylinder, 'kind' | 'capacity' | 'manufacturer' | 'manufacturerSerial' | 'manufacturedOn' | 'lastTestOn' | 'nextTestDue' | 'owner' | 'status' | 'notes'>>;
/**
 * POST /cylinders: a tag read live at the owner warehouse; the serial number is issued by the server and the TID is
 * the Reader's read (never sent).
 */
export type CylinderRegistration = { epc: string } & CylinderPatch;
/** POST /cylinders/:id/retag: the tag read live at the cylinder's location now, and why (kept in the activity log). */
export interface CylinderRetag { epc: string; reason: string }
/** POST /cylinders/:id/found: where it was recorded. */
export interface FoundResult { id: number; serialNo: string; locationId: number; location: string | null }

type Id = number | string | undefined;

export const cylinderPaths = {
  list: (f: CylinderFilter, cursor: Cursor, limit: number) => `/cylinders${query({ ...f, testDue: f.testDue || undefined, dormant: f.dormant || undefined, ...cursor, limit })}`,
  summary: (f: CylinderSummaryFilter) => `/cylinders/summary${query({ ...f })}`,
  /** operator lookup by (part of) the serial number */
  search: (q: string) => `/cylinders${query({ q, limit: FETCH_LIMIT.serialSearch })}`,
  detail: (id: Id) => `/cylinders/${id}`,
  byEpc: (epc: string | undefined) => `/cylinders/by-epc/${encodeURIComponent(epc ?? '')}`,
  journey: (id: number, cursor: Cursor, limit: number) => `/cylinders/${id}/journey${query({ ...cursor, limit })}`,
  events: (id: number, cursor: Cursor<string>, limit: number) => `/cylinders/${id}/events${query({ ...cursor, limit })}`,
  shipments: (id: number, cursor: Cursor, limit: number) => `/cylinders/${id}/shipments${query({ ...cursor, limit })}`,
  /** the whole history, newest first (cursor = the previous page's meta.next) */
  history: (id: number, cursor: string | null, limit: number) => `/cylinders/${id}/history${query({ cursor: cursor ?? undefined, limit })}`,
};

export const registerCylinder = (body: CylinderRegistration) => send<Cylinder>('/cylinders', body);
export const updateCylinder = (id: number, body: CylinderPatch) => send<Cylinder>(`/cylinders/${id}`, body, 'PATCH');
/** Damaged, withdrawn, lost, or active again (needs cylinder.status); the same status again changes nothing. */
export const setCylinderStatus = (id: number, body: CylinderStatusChange) => send<Cylinder>(`/cylinders/${id}/status`, body);
/** Registration mistakes only: 409 CYLINDER_IN_HISTORY once it was ever shipped (`everShipped`). */
export const deleteCylinder = (id: number) => remove(`/cylinders/${id}`);
/** Another tag for the cylinder (cylinder.retag, admins): the old tag is retired for good. */
export const retagCylinder = (id: number, body: CylinderRetag) => send<Cylinder>(`/cylinders/${id}/retag`, body);
/** A missing cylinder read again somewhere is recorded at that location. */
export const markCylinderFound = (id: number) => send<FoundResult>(`/cylinders/${id}/found`);
